import {ChangeEvent, forwardRef, useEffect, useState} from 'react'
import {Calendar, CalendarProps} from '@components/commons/calendar-legacy/Calendar.tsx'
import {InputTextProps} from '@components/commons/input-text/InputText.tsx'
import * as Ariakit from '@ariakit/react'
import {StyledPopover} from '@components/commons/date-picker/style.ts'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {CalendarIcon} from '@/components/ui/icon/Icon'
import dayjs from 'dayjs'
import {Label} from '@/components/ui/label/Label'
import {StyledInputDateCalendarButton, StyledInputDateText} from './style'
import {useTranslation} from '@/translations/i18n'

type InputDateProps = {
    name?: string
    label?: string
    placeholder?: string
    fullWidth?: boolean
    onBlur?: () => void
    onChange?: (value: Date | null) => void
    onDateOutOfPeriod?: () => void
    disabled?: boolean
    errorMessage?: string
    inputProps?: Omit<InputTextProps, 'type'>
    isOptional?: boolean
} & Omit<CalendarProps, 'mode' | 'onDatesChange'>

/**
 * @deprecated use the new InputDate component instead
 */
export const InputDate = forwardRef<HTMLInputElement, InputDateProps>(
    (
        {
            name,
            placeholder,
            selectedDates,
            fullWidth,
            inputProps,
            onBlur,
            onChange,
            disabled,
            label,
            errorMessage,
            isOptional,
            onDateOutOfPeriod,
            ...props
        },
        ref
    ) => {
        const {t} = useTranslation()
        const [inputValue, setInputValue] = useState('')

        const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
            let maskedValue = e.target.value

            // Allow number or slashes
            maskedValue = maskedValue.replace(/[^0-9/]/g, '')

            // Add the slash
            if ((maskedValue.length == 2 || maskedValue.length == 5) && maskedValue.length > inputValue.length) {
                maskedValue += '/'
            }

            if (maskedValue.length <= 10) {
                setInputValue(maskedValue)
            }
        }

        const handleDateChange = (date: Date) => {
            setInputValue(dayjs(date).format('DD/MM/YYYY'))
            onChange?.(date)
        }

        const handleBlur = () => {
            const parsedDate = dayjs(inputValue, 'DD/MM/YYYY', true)

            if (parsedDate.isValid()) {
                // Check if maxDate and minDate are defined and apply boundary checks
                if (
                    (!props.maxDate || parsedDate <= dayjs(props.maxDate)) &&
                    (!props.minDate || parsedDate >= dayjs(props.minDate))
                ) {
                    // The date is valid and within the boundaries
                    const dateObject = parsedDate.toDate()
                    onChange?.(dateObject)
                } else {
                    // The date is not within the allowed boundaries
                    setInputValue('')
                    onChange?.(null)
                    onDateOutOfPeriod?.()
                }
            } else {
                // The date is not valid
                setInputValue('')
                onChange?.(null)
            }
        }

        useEffect(() => {
            setInputValue(selectedDates?.[0] ? dayjs(selectedDates?.[0]).format('DD/MM/YYYY') : '')
        }, [selectedDates])

        return (
            <Flexbox direction="column" gap={1.5} fullWidth={fullWidth}>
                {label && (
                    <Label htmlFor={name}>
                        {label} {isOptional && <span className="description">{t('commons:optional')}</span>}
                    </Label>
                )}
                <Flexbox fullWidth={fullWidth}>
                    <Ariakit.PopoverProvider
                        setOpen={open => {
                            if (!open) {
                                onBlur?.()
                            }
                        }}
                        open={disabled ? false : undefined}
                    >
                        <Ariakit.PopoverDisclosure
                            render={
                                <StyledInputDateCalendarButton
                                    shape="square"
                                    variant="secondary"
                                    $hasError={!!errorMessage}
                                    disabled={disabled}
                                >
                                    <CalendarIcon />
                                </StyledInputDateCalendarButton>
                            }
                        />
                        <StyledPopover gutter={4}>
                            <Calendar
                                {...props}
                                mode={'single'}
                                onDatesChange={dates => handleDateChange(dates[0])}
                                selectedDates={selectedDates}
                            />
                        </StyledPopover>
                    </Ariakit.PopoverProvider>
                    <StyledInputDateText
                        name={name}
                        disabled={disabled}
                        placeholder={placeholder}
                        type="text"
                        onChange={handleInputChange}
                        errorMessage={errorMessage}
                        onBlur={handleBlur}
                        value={inputValue}
                        ref={ref}
                        {...inputProps}
                    />
                </Flexbox>
            </Flexbox>
        )
    }
)
